// Raw API client for the project console.
//
// The console deliberately bypasses the shared baseAxios instance: humps
// camelize/decamelize would silently rewrite user-authored JSON keys —
// stage_weights keyed by stage slugs, pasted response schemas, custom API
// param names, test-call params. Console code therefore talks snake_case
// end-to-end, exactly what the backend serves.

import axios from 'axios';
import { apiErrorMessage, parseApiError } from '@/shared/utils/apiError.js';
import { auth } from '@/shared/utils/auth.js';
import { domain } from '@/shared/utils/request.js';

const raw = axios.create({ baseURL: domain });

// The embedded console (office → /docs-console) gets its token over
// postMessage like every other embed; the shared interceptor only patches
// baseAxios, so without this hook an expired token here stayed a silent
// stream of 401s. Same contract: tell the parent, it re-sends SET_TOKEN.
raw.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      if (error?.response?.status === 401 && window.self !== window.top && !error.config?._retry) {
        error.config._retry = true;
        window.parent.postMessage({ type: 'TOKEN_EXPIRED' }, '*');
      }
    } catch { /* not in a frame */ }
    return Promise.reject(error);
  },
);

export async function consoleRequest(method, url, { data, params, headers, responseType } = {}) {
  try {
    const res = await raw({
      method,
      url,
      data,
      params,
      headers: { ...auth().headers, ...headers },
      responseType,
    });
    return { success: true, response: res.data, status: res.status };
  } catch (e) {
    // No humps on this client, so the envelope arrives snake_case — the
    // shared parser handles both; display text resolves from the code.
    const errorInfo = parseApiError(e);
    const message = apiErrorMessage(errorInfo);
    return { success: false, error: e.response, errorInfo, message, status: e.response?.status };
  }
}

export const api = {
  get: (url, opts) => consoleRequest('GET', url, opts),
  post: (url, opts) => consoleRequest('POST', url, opts),
  patch: (url, opts) => consoleRequest('PATCH', url, opts),
  put: (url, opts) => consoleRequest('PUT', url, opts),
  del: (url, opts) => consoleRequest('DELETE', url, opts),
};

// Authenticated download (the bearer token rides a header, so a plain
// `<a href>` cannot fetch an export): GET as a blob, hand it to the browser.
export async function downloadFile(url, { params, filename } = {}) {
  const res = await consoleRequest('GET', url, { params, responseType: 'blob' });
  if (!res.success) {
    // With responseType 'blob' an error envelope arrives as a Blob too:
    // decode it so the toast shows the code's text, not a generic message.
    const data = res.error?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        const errorInfo = parseApiError({ response: { status: res.status, data: parsed } });
        return { ...res, errorInfo, message: apiErrorMessage(errorInfo) };
      } catch { /* not JSON — keep the generic message */ }
    }
    return res;
  }
  const href = URL.createObjectURL(res.response);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
  return res;
}

// ── Endpoint builders (admin plane, root JWT) ────────────────────────────
export const ep = {
  projects: () => 'project-user/all',

  apiKeys: () => 'api-key',

  signing: (projectId) => `project/${projectId}/signing`,

  indexingSummary: (projectId) => `project/${projectId}/indexing/summary`,
  indexingFiles: (projectId) => `project/${projectId}/indexing/files`,
  indexingRetry: (projectId) => `project/${projectId}/indexing/retry`,
  indexingRebuild: (projectId) => `project/${projectId}/indexing/rebuild`,
  indexingJobs: (projectId) => `project/${projectId}/indexing/jobs`,
  indexingJobCancel: (projectId, jobId) => `project/${projectId}/indexing/jobs/${jobId}/cancel`,
  indexingDoctor: (projectId) => `project/${projectId}/indexing/doctor`,
  indexingCleanup: (projectId) => `project/${projectId}/indexing/cleanup`,
  indexingHealth: (projectId) => `project/${projectId}/indexing/health`,

  settings: (projectId) => `project/${projectId}/settings`,
  policies: (projectId) => `project/${projectId}/policies`,
  policyArea: (projectId, area) => `project/${projectId}/policies/${area}`,

  retention: (projectId) => `project/${projectId}/retention`,
  retentionSections: (projectId) => `project/${projectId}/retention/sections`,
  retentionSection: (projectId, id) => `project/${projectId}/retention/sections/${id}`,
  retentionClasses: (projectId) => `project/${projectId}/retention/classes`,
  retentionClass: (projectId, id) => `project/${projectId}/retention/classes/${id}`,
  retentionReapply: (projectId, id) => `project/${projectId}/retention/classes/${id}/reapply`,
  retentionStandard: (projectId) => `project/${projectId}/retention/standard`,
  retentionStandardImport: (projectId) => `project/${projectId}/retention/standard/import`,
  retentionImport: (projectId) => `project/${projectId}/retention/import`,
  retentionExport: (projectId) => `project/${projectId}/retention/export`,

  webhooks: (projectId) => `project/${projectId}/webhooks`,
  webhook: (projectId, id) => `project/${projectId}/webhooks/${id}`,
  webhookCatalogue: (projectId) => `project/${projectId}/webhooks/catalogue`,
  webhookRules: (projectId) => `project/${projectId}/webhooks/rules`,
  webhookTest: (projectId, id) => `project/${projectId}/webhooks/${id}/test`,
  webhookRotate: (projectId, id) => `project/${projectId}/webhooks/${id}/rotate-secret`,
  webhookDeliveries: (projectId, id) => `project/${projectId}/webhooks/${id}/deliveries`,
  webhookDelivery: (projectId, id, deliveryId) => `project/${projectId}/webhooks/${id}/deliveries/${deliveryId}`,
  webhookRedeliver: (projectId, id, deliveryId) => `project/${projectId}/webhooks/${id}/deliveries/${deliveryId}/redeliver`,
  audit: (projectId) => `project/${projectId}/audit`,
  storage: (projectId) => `project/${projectId}/storage`,
  users: (projectId) => `project/${projectId}/users`,

  connectors: (projectId) => `project/${projectId}/connectors`,
  connector: (projectId, connectorId) => `project/${projectId}/connectors/${connectorId}`,
  connectorApis: (projectId, connectorId) => `project/${projectId}/connectors/${connectorId}/apis`,
  connectorApi: (projectId, serviceId) => `project/${projectId}/connector-apis/${serviceId}`,
  connectorApiTest: (projectId, serviceId) => `project/${projectId}/connector-apis/${serviceId}/test`,
};

// Rough client-side "infer a JSON schema from a sample" used by the
// connectors tab ("infer from sample" paste box).
export function inferSchema(value) {
  if (value === null) return { type: 'null' };
  if (Array.isArray(value)) {
    return { type: 'array', items: value.length ? inferSchema(value[0]) : {} };
  }
  switch (typeof value) {
    case 'string':
      return { type: 'string' };
    case 'number':
      return Number.isInteger(value) ? { type: 'integer' } : { type: 'number' };
    case 'boolean':
      return { type: 'boolean' };
    case 'object': {
      const properties = {};
      Object.entries(value).forEach(([k, v]) => {
        properties[k] = inferSchema(v);
      });
      return { type: 'object', properties };
    }
    default:
      return {};
  }
}
